import { Fragment, lazy, Suspense, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu } from "@base-ui/react/menu";
import {
  Truck,
  FileText,
  Banknote,
  TrendingUp,
  Clock,
  AlertCircle,
  Users,
  Building2,
  CalendarClock,
  Receipt,
  HandCoins,
  SlidersHorizontal,
  Hash,
  FileSearch,
  Zap,
} from "lucide-react";
import { useDashboard } from "@/hooks/useDashboard";
import { Button } from "@/components/ui/button";
import { APP_NAME } from "@/lib/brand";
import { usePermissions } from "@/hooks/usePermissions";
import { useDashboardLayout } from "@/components/dashboard/use-dashboard-layout";
import {
  DashboardEditDrawer,
  type WidgetMeta,
} from "@/components/dashboard/dashboard-edit-drawer";
import {
  FreightReceiveModal,
  type ReceiveTarget,
} from "@/pages/freights/FreightReceiveModal";
import type {
  DashboardData,
  QuickReceived,
  TopClient,
  TopClientByCount,
  TopDriver,
  UnpaidFreight,
  UpcomingInvoice,
} from "@/types/dashboard";
import { cn } from "@/lib/utils";

import { ActionQueueBanner } from "@/components/dashboard/action-queue";

const RevenueChart = lazy(() =>
  import("@/components/dashboard/charts").then((m) => ({ default: m.RevenueChart })),
);
const FreightStatusChart = lazy(() =>
  import("@/components/dashboard/charts").then((m) => ({ default: m.FreightStatusChart })),
);

function formatMoney(value: string | number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value));
}

interface StatCardProps {
  label: string;
  value: string;
  description?: string;
  icon: React.ReactNode;
  variant?: "default" | "pending" | "success" | "danger";
  onClick?: () => void;
  isLoading?: boolean;
}

function StatCard({ label, value, description, icon, variant = "default", onClick, isLoading }: StatCardProps) {
  const colors = {
    default: "border-border bg-card",
    pending: "border-yellow-200 bg-yellow-50",
    success: "border-green-200 bg-green-50",
    danger: "border-red-200 bg-red-50",
  };
  const textColors = {
    default: "text-foreground",
    pending: "text-yellow-700",
    success: "text-green-700",
    danger: "text-red-700",
  };
  const iconColors = {
    default: "text-muted-foreground",
    pending: "text-yellow-500",
    success: "text-green-500",
    danger: "text-red-500",
  };

  return (
    <div
      className={`rounded-lg border p-4 ${colors[variant]} ${onClick ? "cursor-pointer transition-shadow hover:shadow-md" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
    >
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <span className={iconColors[variant]}>{icon}</span>
      </div>
      {isLoading ? (
        <div className="mt-2 space-y-1.5">
          <div className="h-6 w-24 animate-pulse rounded bg-muted" />
          <div className="h-3.5 w-16 animate-pulse rounded bg-muted" />
        </div>
      ) : (
        <>
          <p className={`mt-2 text-2xl font-bold ${textColors[variant]}`}>{value}</p>
          {description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          )}
        </>
      )}
    </div>
  );
}

function Panel({
  title,
  description,
  icon,
  children,
  className,
  action,
}: {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className={`rounded-lg border border-border bg-card p-4 ${className ?? ""}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {icon && <span className="text-muted-foreground shrink-0">{icon}</span>}
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-foreground">{title}</h2>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function PanelSkeleton({ height = 260 }: { height?: number }) {
  return <div className="animate-pulse rounded bg-muted" style={{ height }} />;
}

function TopDriversList({ drivers }: { drivers: TopDriver[] }) {
  if (drivers.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Nenhum frete atribuído nos últimos 6 meses.</p>;
  }
  const max = Math.max(...drivers.map((d) => d.freightCount));
  return (
    <ul className="space-y-2.5">
      {drivers.map((d, i) => (
        <li key={d.id} className="flex items-center gap-3">
          <span className="w-4 text-xs font-semibold text-muted-foreground">{i + 1}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm text-foreground">{d.name}</span>
              <span className="text-xs font-medium text-muted-foreground">{d.freightCount} frete(s)</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-blue-500"
                style={{ width: `${(d.freightCount / max) * 100}%` }}
              />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function TopClientsList({ clients }: { clients: TopClient[] }) {
  if (clients.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Nenhuma fatura emitida ainda.</p>;
  }
  const max = Math.max(...clients.map((c) => Number(c.amount)));
  return (
    <ul className="space-y-2.5">
      {clients.map((c, i) => (
        <li key={c.id} className="flex items-center gap-3">
          <span className="w-4 text-xs font-semibold text-muted-foreground">{i + 1}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm text-foreground">{c.name}</span>
              <span className="text-xs font-medium text-muted-foreground">{formatMoney(c.amount)}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-green-500"
                style={{ width: `${max > 0 ? (Number(c.amount) / max) * 100 : 0}%` }}
              />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function UpcomingInvoicesList({
  invoices,
  onSelect,
}: {
  invoices: UpcomingInvoice[];
  onSelect: (id: string) => void;
}) {
  if (invoices.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Nenhuma fatura vence nos próximos 7 dias.
      </p>
    );
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    <ul className="divide-y divide-border">
      {invoices.map((inv) => {
        const due = new Date(inv.dueDate);
        const daysLeft = Math.round((due.getTime() - today.getTime()) / 86_400_000);
        let dueLabel: string;
        if (daysLeft <= 0) dueLabel = "vence hoje";
        else if (daysLeft === 1) dueLabel = "vence amanhã";
        else dueLabel = `em ${daysLeft} dias`;
        return (
          <li
            key={inv.id}
            className="flex cursor-pointer items-center justify-between gap-3 py-2.5 transition-colors hover:bg-muted/50"
            onClick={() => onSelect(inv.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onSelect(inv.id)}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                #{inv.invoiceNumber} · {inv.companyName}
              </p>
              <p className="text-xs text-muted-foreground">
                {due.toLocaleDateString("pt-BR")} · {dueLabel}
              </p>
            </div>
            <span className="shrink-0 text-sm font-semibold text-foreground">
              {formatMoney(inv.totalAmount)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function UnpaidFreightsList({
  freights,
  onReceive,
}: {
  freights: UnpaidFreight[];
  onReceive: (freight: UnpaidFreight) => void;
}) {
  if (freights.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Nenhum frete entregue pendente de recebimento.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border">
      {freights.map((f) => (
        <li key={f.id} className="flex items-center justify-between gap-3 py-2.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">
              F-{f.code} · {f.companyName}
            </p>
            <p className="text-xs text-muted-foreground">
              {f.deliveredAt
                ? `Entregue ${new Date(f.deliveredAt).toLocaleDateString("pt-BR")}`
                : "Entregue"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tabular-nums text-foreground">
              {formatMoney(f.freightValue)}
            </span>
            <Button size="sm" variant="outline" onClick={() => onReceive(f)}>
              <HandCoins size={14} />
              Receber
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function TopClientsByCountList({ clients }: { clients: TopClientByCount[] }) {
  if (clients.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Nenhum frete cadastrado nos últimos 6 meses.
      </p>
    );
  }
  const max = Math.max(...clients.map((c) => c.freightCount));
  return (
    <ul className="space-y-2.5">
      {clients.map((c, i) => (
        <li key={c.id} className="flex items-center gap-3">
          <span className="w-4 text-xs font-semibold text-muted-foreground">{i + 1}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm text-foreground">{c.name}</span>
              <span className="text-xs font-medium text-muted-foreground">
                {c.freightCount} frete(s)
              </span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-indigo-500"
                style={{ width: `${(c.freightCount / max) * 100}%` }}
              />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  PIX: "PIX",
  BANK_TRANSFER: "Transferência",
  BANK_SLIP: "Boleto",
  CASH: "Dinheiro",
  OTHER: "Outro",
};

function QuickReceivedList({
  items,
  onOpenFreight,
  onOpenInvoice,
}: {
  items: QuickReceived[];
  onOpenFreight: (freightId: string) => void;
  onOpenInvoice: (invoiceId: string) => void;
}) {
  if (items.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Nenhum recebimento via atalho ainda.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border">
      {items.map((item) => {
        const date = new Date(item.paymentDate).toLocaleDateString("pt-BR");
        const method = PAYMENT_METHOD_LABEL[item.method] ?? item.method;
        return (
          <li key={item.paymentId}>
            <Menu.Root>
              <Menu.Trigger
                className="flex w-full items-center justify-between gap-3 py-2.5 text-left transition-colors hover:bg-muted/50"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    F-{item.code} · {item.companyName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {method} · {date}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                  {formatMoney(item.amount)}
                </span>
              </Menu.Trigger>
              <Menu.Portal>
                <Menu.Positioner sideOffset={4} className="z-50">
                  <Menu.Popup className="min-w-[180px] rounded-md border border-border bg-popover p-1 text-sm shadow-md">
                    <Menu.Item
                      onClick={() => onOpenFreight(item.freightId)}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted"
                    >
                      <Truck size={14} className="text-muted-foreground" />
                      Ver frete
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => onOpenInvoice(item.invoiceId)}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted"
                    >
                      <FileSearch size={14} className="text-muted-foreground" />
                      Ver fatura
                    </Menu.Item>
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.Root>
          </li>
        );
      })}
    </ul>
  );
}

const QUICK_RECEIVED_SIZES = [5, 10, 15] as const;
type QuickReceivedSize = (typeof QUICK_RECEIVED_SIZES)[number];

function QuickReceivedSizeSelector({
  value,
  onChange,
}: {
  value: QuickReceivedSize;
  onChange: (next: QuickReceivedSize) => void;
}) {
  return (
    <div className="flex overflow-hidden rounded-md border border-input text-xs">
      {QUICK_RECEIVED_SIZES.map((size) => (
        <button
          key={size}
          type="button"
          onClick={() => onChange(size)}
          className={cn(
            "px-2 py-0.5 transition-colors",
            value === size
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:bg-muted/50",
          )}
        >
          {size}
        </button>
      ))}
    </div>
  );
}

function EmptyState() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
      <Truck size={40} className="mb-4 text-muted-foreground/50" />
      <h2 className="text-base font-semibold text-foreground">Bem-vindo ao {APP_NAME}</h2>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        Comece cadastrando os dados da sua operação para ver os indicadores aqui.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Button size="sm" onClick={() => navigate("/companies/new")}>
          Cadastrar empresa
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate("/drivers/new")}>
          Cadastrar motorista
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate("/freights/new")}>
          Criar frete
        </Button>
      </div>
    </div>
  );
}

interface WidgetDef extends WidgetMeta {
  /** Quando false, o widget é removido do menu de personalização também. */
  available: boolean;
  render: () => React.ReactNode;
}

function buildWidgets(opts: {
  data: DashboardData | undefined;
  isLoading: boolean;
  isOperator: boolean;
  canReceive: boolean;
  navigate: ReturnType<typeof useNavigate>;
  onReceive: (target: UnpaidFreight) => void;
  quickReceivedSize: QuickReceivedSize;
  setQuickReceivedSize: (size: QuickReceivedSize) => void;
}): WidgetDef[] {
  const {
    data,
    isLoading,
    isOperator,
    canReceive,
    navigate,
    onReceive,
    quickReceivedSize,
    setQuickReceivedSize,
  } = opts;
  const widgets: WidgetDef[] = [
    {
      id: "stat.freights-in-transit",
      label: "Fretes em trânsito",
      group: "indicator",
      available: true,
      render: () => (
        <StatCard
          label="Fretes em trânsito"
          value={isLoading ? "" : String(data?.freights.inTransit ?? 0)}
          description={isLoading ? undefined : `${data?.freights.assigned ?? 0} atribuído(s) aguardando`}
          icon={<Truck size={16} />}
          variant="pending"
          isLoading={isLoading}
          onClick={() => navigate("/freights?status=IN_TRANSIT")}
        />
      ),
    },
    {
      id: "stat.freights-pending",
      label: "Fretes pendentes",
      group: "indicator",
      available: true,
      render: () => (
        <StatCard
          label="Fretes pendentes"
          value={isLoading ? "" : String(data?.freights.pending ?? 0)}
          description={isLoading ? undefined : "aguardando atribuição"}
          icon={<Clock size={16} />}
          isLoading={isLoading}
          onClick={() => navigate("/freights?status=PENDING")}
        />
      ),
    },
    {
      id: "stat.invoices-overdue",
      label: "Faturas vencidas",
      group: "indicator",
      available: !isOperator,
      render: () => (
        <StatCard
          label="Faturas vencidas"
          value={isLoading ? "" : String(data?.invoices.overdueCount ?? 0)}
          description={isLoading ? undefined : "não pagas no prazo"}
          icon={<AlertCircle size={16} />}
          variant={!isLoading && (data?.invoices.overdueCount ?? 0) > 0 ? "danger" : "default"}
          isLoading={isLoading}
          onClick={() => navigate("/invoices")}
        />
      ),
    },
    {
      id: "stat.repayments-pending",
      label: "Repasses pendentes",
      group: "indicator",
      available: true,
      render: () => (
        <StatCard
          label="Repasses pendentes"
          value={isLoading ? "" : formatMoney(data?.repayments.pendingAmount ?? "0")}
          description={isLoading ? undefined : `${data?.repayments.pendingCount ?? 0} repasse(s)`}
          icon={<Banknote size={16} />}
          variant="pending"
          isLoading={isLoading}
          onClick={() => navigate("/repayments?status=PENDING")}
        />
      ),
    },
    {
      id: "stat.invoices-monthly",
      label: "Faturado no mês",
      group: "indicator",
      available: !isOperator,
      render: () => (
        <StatCard
          label="Faturado no mês"
          value={isLoading ? "" : formatMoney(data?.invoices.monthlyAmount ?? "0")}
          description={isLoading ? undefined : `${data?.invoices.monthlyCount ?? 0} fatura(s) emitida(s)`}
          icon={<FileText size={16} />}
          isLoading={isLoading}
          onClick={() => navigate("/invoices")}
        />
      ),
    },
    {
      id: "stat.margin-monthly",
      label: "Margem do mês",
      description: "Receita − pago a motoristas (fretes concluídos)",
      group: "indicator",
      available: !isOperator,
      render: () => (
        <StatCard
          label="Margem do mês"
          value={isLoading ? "" : formatMoney(data?.margin?.amount ?? "0")}
          description={
            isLoading ? undefined : `${data?.margin?.freightCount ?? 0} frete(s) concluído(s)`
          }
          icon={<TrendingUp size={16} />}
          variant="success"
          isLoading={isLoading}
        />
      ),
    },
    {
      id: "stat.expenses-monthly",
      label: "Despesas operacionais do mês",
      description: "Combustível, manutenção, pedágio e outras saídas",
      group: "indicator",
      available: !isOperator,
      render: () => (
        <StatCard
          label="Despesas no mês"
          value={isLoading ? "" : formatMoney(data?.operationalExpenses?.monthlyAmount ?? "0")}
          description={
            isLoading
              ? undefined
              : `${data?.operationalExpenses?.monthlyCount ?? 0} lançamento(s)`
          }
          icon={<Receipt size={16} />}
          variant="danger"
          isLoading={isLoading}
          onClick={() => navigate("/operational-expenses")}
        />
      ),
    },
    {
      id: "panel.revenue-chart",
      label: "Faturamento × recebimento",
      description: "Linha dos últimos 6 meses",
      group: "panel",
      available: !isOperator,
      render: () => (
        <Panel
          title="Faturamento × recebimento"
          description="Últimos 6 meses"
          icon={<TrendingUp size={16} />}
          className="lg:col-span-2"
        >
          {isLoading || !data?.monthlyRevenue ? (
            <PanelSkeleton />
          ) : (
            <Suspense fallback={<PanelSkeleton />}>
              <RevenueChart data={data.monthlyRevenue} />
            </Suspense>
          )}
        </Panel>
      ),
    },
    {
      id: "panel.freight-status-chart",
      label: "Fretes por status",
      description: "Pizza com a distribuição atual",
      group: "panel",
      available: true,
      render: () => (
        <Panel title="Fretes por status" icon={<Truck size={16} />}>
          {isLoading || !data ? (
            <PanelSkeleton />
          ) : (
            <Suspense fallback={<PanelSkeleton />}>
              <FreightStatusChart freights={data.freights} />
            </Suspense>
          )}
        </Panel>
      ),
    },
    {
      id: "panel.unpaid-freights",
      label: "Fretes a receber",
      description: "Lista curta com botão de recebimento rápido",
      group: "panel",
      available: !isOperator && canReceive,
      render: () => (
        <Panel
          title="Fretes a receber"
          description="Entregues, ainda não recebidos"
          icon={<HandCoins size={16} />}
          action={
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate("/freights?paymentStatus=unpaid")}
            >
              Ver tudo
            </Button>
          }
        >
          {isLoading || !data?.unpaidFreights ? (
            <PanelSkeleton height={160} />
          ) : (
            <UnpaidFreightsList freights={data.unpaidFreights} onReceive={onReceive} />
          )}
        </Panel>
      ),
    },
    {
      id: "panel.top-drivers",
      label: "Top motoristas",
      description: "Ranking por nº de fretes (6 meses)",
      group: "panel",
      available: true,
      render: () => (
        <Panel
          title="Top motoristas"
          description="Por nº de fretes (últimos 6 meses)"
          icon={<Users size={16} />}
        >
          {isLoading || !data ? (
            <PanelSkeleton height={160} />
          ) : (
            <TopDriversList drivers={data.topDrivers} />
          )}
        </Panel>
      ),
    },
    {
      id: "panel.top-clients",
      label: "Top clientes",
      description: "Ranking por valor faturado",
      group: "panel",
      available: !isOperator,
      render: () => (
        <Panel
          title="Top clientes"
          description="Por valor total faturado"
          icon={<Building2 size={16} />}
        >
          {isLoading || !data?.topClients ? (
            <PanelSkeleton height={160} />
          ) : (
            <TopClientsList clients={data.topClients} />
          )}
        </Panel>
      ),
    },
    {
      id: "panel.top-clients-by-count",
      label: "Top clientes (por volume)",
      description: "Ranking por nº de fretes (6 meses)",
      group: "panel",
      available: true,
      render: () => (
        <Panel
          title="Top clientes por volume"
          description="Por nº de fretes (últimos 6 meses)"
          icon={<Hash size={16} />}
        >
          {isLoading || !data ? (
            <PanelSkeleton height={160} />
          ) : (
            <TopClientsByCountList clients={data.topClientsByCount} />
          )}
        </Panel>
      ),
    },
    {
      id: "panel.quick-received",
      label: "Recebimentos rápidos",
      description: 'Últimos fretes pagos via atalho "Receber pagamento"',
      group: "panel",
      available: !isOperator,
      render: () => (
        <Panel
          title="Recebimentos rápidos"
          description="Pagos via atalho, sem fatura formal"
          icon={<Zap size={16} />}
          action={
            <QuickReceivedSizeSelector
              value={quickReceivedSize}
              onChange={setQuickReceivedSize}
            />
          }
        >
          {isLoading || !data?.recentQuickReceived ? (
            <PanelSkeleton height={160} />
          ) : (
            <QuickReceivedList
              items={data.recentQuickReceived.slice(0, quickReceivedSize)}
              onOpenFreight={(id) => navigate(`/freights/${id}`)}
              onOpenInvoice={(id) => navigate(`/invoices/${id}`)}
            />
          )}
        </Panel>
      ),
    },
    {
      id: "panel.upcoming-invoices",
      label: "Faturas a vencer",
      description: "Próximos 7 dias",
      group: "panel",
      available: !isOperator,
      render: () => (
        <Panel
          title="Faturas a vencer"
          description="Próximos 7 dias"
          icon={<CalendarClock size={16} />}
        >
          {isLoading || !data?.upcomingInvoices ? (
            <PanelSkeleton height={120} />
          ) : (
            <UpcomingInvoicesList
              invoices={data.upcomingInvoices}
              onSelect={(id) => navigate(`/invoices/${id}`)}
            />
          )}
        </Panel>
      ),
    },
  ];

  return widgets;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useDashboard();
  const { has } = usePermissions();
  const layout = useDashboardLayout();
  const [editOpen, setEditOpen] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState<UnpaidFreight | null>(null);

  const isOperator = !has("reports.view");
  const canReceive = has("invoice.pay");

  const isEmpty =
    !isLoading &&
    data &&
    data.freights.total === 0 &&
    (isOperator || (data.invoices.monthlyCount ?? 0) === 0) &&
    data.repayments.pendingCount === 0;

  const quickReceivedSize = layout.getSetting<QuickReceivedSize>(
    "panel.quick-received.size",
    10,
  );
  const setQuickReceivedSize = (size: QuickReceivedSize) =>
    layout.setSetting("panel.quick-received.size", size);

  const widgets = useMemo(
    () =>
      buildWidgets({
        data,
        isLoading,
        isOperator,
        canReceive,
        navigate,
        onReceive: setReceiveTarget,
        quickReceivedSize,
        setQuickReceivedSize,
      }),
    // setQuickReceivedSize muda referência a cada render mas é estável em
    // comportamento; quickReceivedSize sim é o gatilho real.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, isLoading, isOperator, canReceive, navigate, quickReceivedSize],
  );

  const availableWidgets = widgets.filter((w) => w.available);
  const visibleIndicators = availableWidgets.filter(
    (w) => w.group === "indicator" && !layout.isHidden(w.id),
  );
  const visiblePanels = availableWidgets.filter(
    (w) => w.group === "panel" && !layout.isHidden(w.id),
  );

  const receiveTargetForModal: ReceiveTarget | null = receiveTarget
    ? {
        id: receiveTarget.id,
        code: receiveTarget.code,
        freightValue: receiveTarget.freightValue,
        company: { tradeName: receiveTarget.companyName },
      }
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Visão geral da operação</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
          <SlidersHorizontal size={14} />
          Personalizar
        </Button>
      </div>

      {isEmpty ? (
        <EmptyState />
      ) : (
        <>
          <ActionQueueBanner
            data={data}
            isLoading={isLoading}
            showFinancialItems={!isOperator}
          />

          {visibleIndicators.length > 0 && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {visibleIndicators.map((w) => (
                <Fragment key={w.id}>{w.render()}</Fragment>
              ))}
            </div>
          )}

          {visiblePanels.length > 0 && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {visiblePanels.map((w) => (
                <Fragment key={w.id}>{w.render()}</Fragment>
              ))}
            </div>
          )}
        </>
      )}

      <DashboardEditDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        widgets={availableWidgets.map(({ id, label, description, group }) => ({
          id,
          label,
          description,
          group,
        }))}
        isHidden={layout.isHidden}
        onToggle={layout.toggle}
        onReset={layout.reset}
      />

      {receiveTargetForModal && (
        <FreightReceiveModal
          freight={receiveTargetForModal}
          open={!!receiveTarget}
          onClose={() => setReceiveTarget(null)}
        />
      )}
    </div>
  );
}
